import { ObjectIdLookup } from 'src/app/models/objectIdLookup.model';

/**
 * Grouping and labelling for the Role Permissions matrix (DASH-3179, design-spec §4.4 / §9).
 *
 * Grouping is driven entirely by the backend `category` field on {@link ObjectIdLookup} — never by
 * a frontend catalogue (a frontend taxonomy would inherit the drift that already left the old
 * `rolePermissionObjects.constants.ts` 11 objects behind the backend). The only frontend concern
 * here is the deterministic DISPLAY ORDER of the groups; membership and labels come from the API.
 */

/** Frontend-only group for objects the backend returned with a null/blank category. */
export const OTHER_GROUP = 'Other';

/** Backend category value that tenant custom objects (`COID_*`) arrive under. */
export const CUSTOM_OBJECTS_GROUP = 'Custom Objects';

/**
 * Deterministic display order of the platform categories (design-spec §4.4, revised 2026-08-23).
 * These strings are the backend `RolePermissionObjectCategories` VALUES — this array only fixes the
 * order they render in, it is not a source of group membership. A category the backend emits that is
 * not listed here still renders (appended after the known platform groups), so a new backend
 * category can never silently drop objects.
 */
export const PLATFORM_CATEGORY_ORDER: readonly string[] = [
    'Sampling & Surveys',
    'Laboratory',
    'Agents & Exposure Limits',
    'Workforce & Exposure Groups',
    'Risk & Controls',
    'Equipment & Calibration',
    'Real-Time Data',
    'Mapping & Zones',
    'Alerts & Notifications',
    'Reporting & Content',
    'Administration & Access',
    'AI Analyst & Credits',
];

export interface PermissionObjectGroup {
    /** The category key (backend value, or {@link OTHER_GROUP} for null-category objects). */
    category: string;
    /** Human-readable header — identical to {@link category}; the backend values are display-ready. */
    displayName: string;
    objects: ObjectIdLookup[];
}

/**
 * Resolve the display label for an object. Prefers the backend `displayName`, falls back to the
 * machine-derived `name`, and NEVER falls back to the raw `GLOBAL_*` / `COID_*` id (design-spec §9,
 * DS-13). Replaces the old `getObjectName()` id fallback branch.
 */
export function getObjectDisplayName(object: ObjectIdLookup): string {
    return object.displayName?.trim() || object.name?.trim() || '';
}

/** Category an object belongs to, mapping a null/blank backend category to the "Other" group. */
export function resolveCategory(object: ObjectIdLookup): string {
    const category = object.category?.trim();
    return category ? category : OTHER_GROUP;
}

/**
 * Order index for a group. Platform categories sort by {@link PLATFORM_CATEGORY_ORDER}; any unknown
 * platform category sorts after them (alphabetically, by falling to the same bucket); then "Other";
 * then "Custom Objects" last. Empty groups are never produced, so a tenant with no custom objects
 * shows no empty Custom Objects group (design-spec §4.5).
 */
function groupSortIndex(category: string): number {
    const known = PLATFORM_CATEGORY_ORDER.indexOf(category);
    if (known !== -1) {
        return known;
    }
    if (category === CUSTOM_OBJECTS_GROUP) {
        return PLATFORM_CATEGORY_ORDER.length + 2;
    }
    if (category === OTHER_GROUP) {
        return PLATFORM_CATEGORY_ORDER.length + 1;
    }
    // Unknown backend category: after known platform groups, before Other/Custom Objects.
    return PLATFORM_CATEGORY_ORDER.length;
}

/**
 * Group objects by their backend category into the deterministic design order. Objects keep their
 * backend order within a group. Groups with no objects are omitted entirely.
 */
export function groupPermissionObjects(objects: ObjectIdLookup[]): PermissionObjectGroup[] {
    const byCategory = new Map<string, ObjectIdLookup[]>();

    for (const object of objects ?? []) {
        const category = resolveCategory(object);
        const bucket = byCategory.get(category);
        if (bucket) {
            bucket.push(object);
        } else {
            byCategory.set(category, [object]);
        }
    }

    return Array.from(byCategory.entries())
        .map(([category, groupObjects]) => ({ category, displayName: category, objects: groupObjects }))
        .sort((a, b) => {
            const delta = groupSortIndex(a.category) - groupSortIndex(b.category);
            return delta !== 0 ? delta : a.category.localeCompare(b.category);
        });
}

/**
 * Count of objects in a set that have any permission granted, for the group header `granted / total`
 * chip. "Granted" means a non-zero mask; a persisted row at `Permissions = 0` (revoke ≠ delete,
 * design-spec §8) counts as not granted.
 */
export function countGrantedObjects(
    objects: ObjectIdLookup[],
    permissionsById: Map<string, number>,
): number {
    return (objects ?? []).reduce(
        (count, object) => count + ((permissionsById.get(object.id) ?? 0) !== 0 ? 1 : 0),
        0,
    );
}
